/**
 * Voice Enhancer — WebAudio-based real-time audio processing for microphone
 * capture. Improves clarity, presence, and warmth of the outgoing mic stream
 * for hosts and co-hosts in live streams and meeting rooms.
 *
 * How it works:
 *   Wraps `navigator.mediaDevices.getUserMedia` so ANY caller that requests an
 *   audio track (Stream Video SDK, MediaRecorder, custom recorders, etc.)
 *   automatically gets an enhanced MediaStream when enhancement is enabled.
 *
 * Processing chain (48 kHz):
 *   src → HighPass(80Hz)  ← remove rumble / desk thump
 *       → LowShelf(200Hz +2dB)  ← warmth
 *       → PeakingEQ(3kHz +3dB, Q 1.2)  ← vocal presence
 *       → HighShelf(9kHz +1.5dB)  ← air / clarity
 *       → DynamicsCompressor(-24dB, 4:1)  ← even loudness
 *       → makeup Gain(+3.5dB)  ← restore level
 *       → destination
 *
 * Enable/disable at runtime via `setVoiceEnhancement(bool)` — cheap toggle.
 * The AudioContext is reused across streams to avoid repeated allocations.
 */

let audioCtx = null;
let enabled = true;
let auxGain = null;  // AI announcer / SFX bus mixed into every outgoing stream
const originals = new WeakMap();

import { createEffectsChain, effectsActive } from "./voiceEffects";

function ensureCtx() {
  if (audioCtx) return audioCtx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  audioCtx = new Ctx({ latencyHint: "interactive" });
  return audioCtx;
}

/** Shared aux input: anything connected here is heard by stream viewers. */
export function getAuxInput() {
  const ctx = ensureCtx();
  if (!auxGain) { auxGain = ctx.createGain(); auxGain.gain.value = 1.0; }
  return { ctx, input: auxGain };
}

/** Build the processing graph once per stream. Returns the enhanced MediaStream. */
function buildEnhancedStream(rawStream) {
  const audioTracks = rawStream.getAudioTracks();
  if (audioTracks.length === 0) return rawStream;

  const ctx = ensureCtx();
  const src = ctx.createMediaStreamSource(new MediaStream(audioTracks));

  let node = src;
  if (enabled) {
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass"; hp.frequency.value = 80; hp.Q.value = 0.7;

    const warmth = ctx.createBiquadFilter();
    warmth.type = "lowshelf"; warmth.frequency.value = 200; warmth.gain.value = 2;

    const presence = ctx.createBiquadFilter();
    presence.type = "peaking"; presence.frequency.value = 3000;
    presence.Q.value = 1.2; presence.gain.value = 3;

    const air = ctx.createBiquadFilter();
    air.type = "highshelf"; air.frequency.value = 9000; air.gain.value = 1.5;

    const compressor = ctx.createDynamicsCompressor();
    compressor.threshold.value = -24; compressor.knee.value = 8;
    compressor.ratio.value = 4;
    compressor.attack.value = 0.003; compressor.release.value = 0.25;

    const makeup = ctx.createGain();
    makeup.gain.value = 1.5;  // ~+3.5 dB

    src.connect(hp);
    hp.connect(warmth); warmth.connect(presence);
    presence.connect(air); air.connect(compressor);
    compressor.connect(makeup);
    node = makeup;
  }

  // Voice Effects Library chain (voice changer + reverb + modes + mic volume
  // + ear monitoring). Live-tunable — no mic re-acquire needed.
  const fx = createEffectsChain(ctx, node);

  const dest = ctx.createMediaStreamDestination();
  fx.output.connect(dest);
  getAuxInput().input.connect(dest);

  // Combine enhanced audio with any video tracks from the original stream.
  const outStream = new MediaStream();
  dest.stream.getAudioTracks().forEach((t) => outStream.addTrack(t));
  rawStream.getVideoTracks().forEach((t) => outStream.addTrack(t));
  outStream.__rawStream = rawStream;

  return outStream;
}

/** Install a global getUserMedia interceptor exactly once per page load. */
export function installVoiceEnhancer() {
  if (!navigator?.mediaDevices?.getUserMedia) return;
  if (navigator.mediaDevices.__voiceEnhancerInstalled) return;
  const original = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);
  originals.set(navigator.mediaDevices, original);

  navigator.mediaDevices.getUserMedia = async function patched(constraints) {
    // Force browser-native aids too — belt & suspenders.
    const audioConstraint = constraints?.audio;
    if (audioConstraint && audioConstraint !== false) {
      const baseAudio = typeof audioConstraint === "object" ? audioConstraint : {};
      constraints = {
        ...constraints,
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          ...baseAudio,
        },
      };
    }

    const raw = await original(constraints);
    if (!constraints?.audio) return raw;
    if (!enabled && !effectsActive()) return raw;
    try {
      return buildEnhancedStream(raw);
    } catch (e) {
      console.warn("[voiceEnhancer] fell back to raw stream:", e);
      return raw;
    }
  };
  navigator.mediaDevices.__voiceEnhancerInstalled = true;
}

/** Uninstall the interceptor (mainly for tests). */
export function uninstallVoiceEnhancer() {
  const original = originals.get(navigator.mediaDevices);
  if (original) {
    navigator.mediaDevices.getUserMedia = original;
    navigator.mediaDevices.__voiceEnhancerInstalled = false;
  }
}

/** Turn enhancement on/off at runtime. Existing streams are unaffected — the
 *  toggle only impacts NEW getUserMedia calls. Use `restartAudio(call)` from
 *  the caller side to actually re-acquire the mic. */
export function setVoiceEnhancement(on) {
  enabled = !!on;
}

export function isVoiceEnhancementEnabled() {
  return enabled;
}
